import { mockDelay } from '@/services/factory'
import type {
  AssistantWorkItem,
  CreateAssistantWorkInput,
  CreateResearchMaterialInput,
  ResearchMaterial,
  UpdateAssistantWorkInput,
} from '@/types/assistantWork.types'
import type { AssistantWorkService } from './assistantWork.service'

const now = () => new Date().toISOString()
const id = () => crypto.randomUUID()

export function createMockAssistantWorkService(): AssistantWorkService {
  const work: AssistantWorkItem[] = []
  const research: ResearchMaterial[] = []
  return {
    async listWork(status, persona) {
      await mockDelay()
      return work.filter((item) => (!status || item.status === status) && (!persona || item.assignedPersona === persona))
    },
    async createWork(input: CreateAssistantWorkInput) {
      await mockDelay()
      const item: AssistantWorkItem = {
        id: id(), tenantId: 'mock-tenant', ...input,
        assignedByUserId: 'mock-manager', riskLevel: input.riskLevel ?? 'standard',
        workValue: input.workValue ?? 0, status: 'assigned', createdAt: now(), updatedAt: now(),
      }
      work.unshift(item)
      return item
    },
    async updateWork(workId: string, input: UpdateAssistantWorkInput) {
      await mockDelay()
      const item = work.find((candidate) => candidate.id === workId)
      if (!item) throw new Error('Assistant work item not found')
      Object.assign(item, input, { status: 'in_progress', updatedAt: now() })
      return item
    },
    async submitWork(workId, handoffNotes) {
      await mockDelay()
      const item = work.find((candidate) => candidate.id === workId)
      if (!item) throw new Error('Assistant work item not found')
      Object.assign(item, { handoffNotes, status: 'review_requested', updatedAt: now() })
      return item
    },
    async reviewWork(workId, decision, notes) {
      await mockDelay()
      const item = work.find((candidate) => candidate.id === workId)
      if (!item) throw new Error('Assistant work item not found')
      Object.assign(item, { status: decision, reviewNotes: notes, reviewerId: 'mock-manager', reviewedAt: now(), updatedAt: now() })
      return item
    },
    async listResearch(status) {
      await mockDelay()
      return research.filter((item) => !status || item.status === status)
    },
    async createResearch(input: CreateResearchMaterialInput) {
      await mockDelay()
      const item: ResearchMaterial = {
        id: id(), tenantId: 'mock-tenant', ...input,
        riskLevel: input.riskLevel ?? 'standard', workValue: input.workValue ?? 0,
        status: 'draft', createdBy: 'mock-researcher', createdAt: now(), updatedAt: now(),
      }
      research.unshift(item)
      return item
    },
    async submitResearch(materialId) {
      await mockDelay()
      const item = research.find((candidate) => candidate.id === materialId)
      if (!item) throw new Error('Research material not found')
      Object.assign(item, { status: 'review_requested', updatedAt: now() })
      return item
    },
    async reviewResearch(materialId, decision, notes) {
      await mockDelay()
      const item = research.find((candidate) => candidate.id === materialId)
      if (!item) throw new Error('Research material not found')
      Object.assign(item, { status: decision, reviewNotes: notes, reviewerId: 'mock-manager', reviewedAt: now(), updatedAt: now() })
      return item
    },
  }
}
